import React, { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Icon from '../../../../../Icon';
import { formatDate } from '../../../../utils';
import PrivacyIcon from '../../../../../PrivacyIcon';
import { ACTIVITY_LOG_USER_ACTION_TYPE } from '../../../../constants';
import { targetUserIdSelector } from '../../../../../../redux/context/selectors';
import { actions as loggingActionRequest } from '../../../../../../redux/healthboard/patientPortal/logging-action-req';

import styles from '../index.styl';
import localStyles from './index.styl';

const { VIEW: actionType } = ACTIVITY_LOG_USER_ACTION_TYPE;

const EncounterCard = ({ entity, formatMessage }) => {
  const dispatch = useDispatch();
  const userId = useSelector(targetUserIdSelector);
  const [activeCard, setActiveCard] = useState(false);

  const {
    date,
    description,
    diagnosis,
    providerLocation,
    providerProfile,
    isPrivate,
    ccdaUserDocuments = [],
  } = { ...entity };

  const { firstName, lastName, credential } = providerProfile || {};

  const safeProviderName = `${firstName || ''} ${lastName || ''}${
    credential ? `, ${credential}` : ''
  }`;

  const documents = useMemo(
    () => ccdaUserDocuments.filter(({ files = [] }) => Boolean(files.length)),
    [ccdaUserDocuments],
  );

  const viewDocument = useCallback(
    ({ url, documentUid }) => {
      window.open(url);
      dispatch(loggingActionRequest.send({ userId, documentUid, actionType }));
    },
    [userId],
  );

  const cardData = [
    {
      content: providerLocation,
      meta: formatMessage({ id: 'profile.fields.location' }),
    },
    {
      content: diagnosis,
      meta: formatMessage({ id: 'patient.portal.diagnosis' }),
    },
    {
      meta: formatMessage({ id: 'common.documents' }),
      content:
        documents.length &&
        documents.map(({ files, filename, documentUid, title }) =>
          files.map(({ url, extension }) => (
            <div
              key={url}
              className={localStyles.file}
              onClick={() => viewDocument({ url, documentUid })}
            >
              <Icon size={32} name="icon-document-black" />
              <span className={localStyles.filename}>
                {title || `${filename.split('.')[0]}.${extension}`}
              </span>
            </div>
          )),
        ),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.leftSideWrapper}>
          <Icon
            size={28}
            name="icon-encounters-black"
            className={styles.iconType}
          />
          <div className={styles.contentWrapper}>
            <div className={styles.title}>
              <div
                className={cn(styles.titleWrapper, {
                  [styles.noTitle]: !description,
                })}
              >
                {description}
              </div>
              <PrivacyIcon show={isPrivate} className={styles.privacyIcon} />
            </div>
            <div className={styles.descriptionWrapper}>
              <div className={styles.description}>{safeProviderName}</div>
              <div className={styles.description}>
                {date ? formatDate(date, 'MMM D, Y h:mm A') : ''}
              </div>
            </div>
          </div>
        </div>
        <Icon
          size={16}
          name={activeCard ? 'icon-arrow-up' : 'icon-arrow-down'}
          className={styles.icon}
          onClick={() => setActiveCard(!activeCard)}
        />
      </div>
      {activeCard && (
        <div className={localStyles.card}>
          {cardData.map(({ meta, content }) => (
            <div style={{ display: 'flex' }}>
              <div className={cn(styles.meta, localStyles.meta)}>{meta}</div>
              <div className={cn(styles.fullContent, localStyles.content)}>
                {content || <span>&#8212;</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

EncounterCard.propTypes = {
  entity: PropTypes.object,
  formatMessage: PropTypes.func,
};

export default EncounterCard;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/Card/EncounterCard/index.jsx